import { DndContext, MouseSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext } from "@dnd-kit/sortable";
import { memo, useCallback, useMemo } from "react";

import type { QuestionType } from "@/lib/gql/__generated__/graphql";
import { type QuestionItemInput } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";

import { QuestionPreQuestionRow } from "./pre-question-row";
import { SortablePreQuestionRow } from "./sortable-pre-question-row";
import { QuestionCardItemsWrapper } from "../../../system/question/card/card-items-wrapper";

type Props = {
	questions: ForArrayType<QuestionItemInput[]>;

	appendQuestion: () => void;
	removeQuestion: (index: number) => () => void;
	reorderQuestions: (activeId: string, overId: string) => void;

	onSetIsRequired: (index: number) => (isRequired: boolean) => void;
	onSetQuestionType: (index: number) => (input: QuestionType) => void;
	onSetTitle: (index: number) => (input: string) => void;

	appendOptionItem: (index: number) => () => void;

	removeOptionItem: (index: number) => (optionIndex: number) => void;

	onSetOptionItem: (index: number) => (optionIndex: number) => (input: string) => void;

	title?: string;
	guide?: string;
};

export const QuestionPreQuestions = memo(
	({
		questions,
		appendQuestion,
		removeQuestion,
		reorderQuestions,
		onSetIsRequired,
		onSetQuestionType,
		onSetTitle,

		appendOptionItem,
		removeOptionItem,

		onSetOptionItem,
		title,
		guide,
	}: Props) => {
		const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 5 } });
		const sensors = useSensors(mouseSensor);

		const questionIds = useMemo(
			() => questions.value.map((item) => item.questionID.value),
			[questions.value],
		);

		const handleDragEnd = useCallback(
			({ active, over }: DragEndEvent) => {
				if (!over || active.id === over.id) {
					return;
				}
				reorderQuestions(String(active.id), String(over.id));
			},
			[reorderQuestions],
		);

		const canReorder = questions.value.length > 1;

		const rowProps = (index: number) => ({
			remove: removeQuestion(index),
			onSetQuestionType: onSetQuestionType(index),
			onSetTitle: onSetTitle(index),
			onSetIsRequired: onSetIsRequired(index),
			appendOptionItem: appendOptionItem(index),
			removeOptionItem: removeOptionItem(index),
			onSetOptionItem: onSetOptionItem(index),
		});

		return (
			<FeatureIslandCategoryQuestionForMain
				required={false}
				title={title ?? "限定の事前質問"}
				hasError={questions.hasError}
				isEdited={questions.isEdited}
				guide={guide}
				inputChildren={
					<QuestionCardItemsWrapper appendItem={appendQuestion}>
						{canReorder ? (
							<>
								<p className="text-sm text-caption-50">
									左端の並べ替えアイコンをドラッグして、質問の表示順を変更できます。
								</p>
								<DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
									<SortableContext items={questionIds}>
										{questions.value.map((item, index) => {
											return (
												<SortablePreQuestionRow
													key={item.questionID.value}
													question={item}
													{...rowProps(index)}
												/>
											);
										})}
									</SortableContext>
								</DndContext>
							</>
						) : (
							questions.value.map((item, index) => {
								return (
									<QuestionPreQuestionRow
										key={item.questionID.value}
										question={item}
										{...rowProps(index)}
									/>
								);
							})
						)}
					</QuestionCardItemsWrapper>
				}
			/>
		);
	},
);

QuestionPreQuestions.displayName = "QuestionPreQuestions";
